import React, { useCallback, useRef } from 'react'
import { useDrop } from 'react-dnd'
import { DRAG_TYPE } from '../WidgetFrame'

/**
 * Where in this column a drop would land, as an index into the column's
 * remaining cards.
 *
 * The card being dragged is rendered `hidden`, so it has no box and is filtered
 * out here - which means the index this returns is already an index into the
 * list *after* the widget has been lifted out. No off-by-one correction needed
 * when dragging within the same column.
 */
const insertionIndexAt = (listElement, clientY) => {
  const cards = [...listElement.querySelectorAll('[data-card]')]
    .map((node) => node.getBoundingClientRect())
    .filter((rect) => rect.height > 0)

  for (let i = 0; i < cards.length; i++) {
    if (clientY < cards[i].top + cards[i].height / 2) return i
  }

  return cards.length
}

/**
 * One of the dashboard's three columns.
 *
 * Each column is an independent stack, so a tall widget only ever pushes down
 * the widgets beneath it in its own column. That is the whole reason this is
 * three lists rather than one CSS grid: grid rows share a height, so a 25,000px
 * widget would strand everything on the row beside it 25,000px down the page.
 *
 * The column - not the individual card - is the drop target, which is what makes
 * a drop insert between two widgets instead of swapping with one.
 */
const DashboardColumn = ({
  columnIndex,
  widthClass,
  count,
  customizing,
  dropIndex,
  onHover,
  onDrop,
  children,
}) => {
  const listRef = useRef(null)

  const [{ isOver }, drop] = useDrop({
    accept: DRAG_TYPE,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    hover(item, monitor) {
      if (!customizing || !listRef.current) return

      const offset = monitor.getClientOffset()
      if (!offset) return

      onHover(columnIndex, insertionIndexAt(listRef.current, offset.y))
    },
    drop(item, monitor) {
      if (!listRef.current) return

      const offset = monitor.getClientOffset()
      const index = offset ? insertionIndexAt(listRef.current, offset.y) : count

      onDrop(item, columnIndex, index)
    },
  })

  const attach = useCallback((node) => {
    listRef.current = node
    if (customizing) drop(node)
  }, [drop, customizing])

  const indicator = (
    <li aria-hidden="true" className="-my-1 h-1 rounded-full bg-primary" />
  )

  // Interleave the drop indicator at the insertion point.
  const cards = React.Children.toArray(children)
  const withIndicator = []

  cards.forEach((card, i) => {
    if (dropIndex === i) withIndicator.push(React.cloneElement(indicator, { key: 'drop' }))
    withIndicator.push(card)
  })

  if (dropIndex !== null && dropIndex >= cards.length) {
    withIndicator.push(React.cloneElement(indicator, { key: 'drop' }))
  }

  return (
    <div className={`w-full ${widthClass}`}>
      {customizing && (
        <div className="mb-2 flex items-baseline justify-between px-1 text-xs text-gray-500">
          <span className="font-medium uppercase tracking-wide">Column {columnIndex + 1}</span>
          <span>{count === 1 ? '1 widget' : `${count} widgets`}</span>
        </div>
      )}

      <ul
        ref={attach}
        className={`list-none space-y-4 ${
          customizing
            ? `min-h-[6rem] rounded border-2 border-dashed p-2 transition ${
                isOver ? 'border-primary bg-white' : 'border-gray-400'
              }`
            : ''
        }`}
      >
        {withIndicator}

        {customizing && count === 0 && (
          <li className="py-6 text-center text-xs text-gray-400">Drop a widget here</li>
        )}
      </ul>
    </div>
  )
}

export default DashboardColumn
